// The wholesale enquiry form.
//
// It sends through the SAME route as the contact form in
// src/pages/home/components/Visit.tsx — POST /api/contact — and arrives in the
// same inbox, labelled "Wholesale" by its topic. It also keeps the same two
// rules that form keeps, and they are not negotiable in a copy:
//
//   1. Never tell a visitor a message went through unless it did. Anything we
//      cannot read as a success is "down", and "down" shows ReachUs, not a
//      thank-you.
//   2. Never ask for a verification the page cannot show. If the widget cannot
//      render, the Send button goes with it and the email address takes over.
//
// Because it shares the route, it starts working at the same moment the contact
// form does — when its bot check and Resend are configured (DEPLOYMENT.md §2) — and
// not one minute before.

import { useState } from "react";
import ReachUs from "@/components/ReachUs";
import Altcha from "@/components/Altcha";

type FormState = {
  // "down" means the send did not happen and we do not know when it will —
  // the visitor is shown a way to reach a human, never a thank-you.
  status: "idle" | "loading" | "success" | "error" | "down";
  message: string;
};

export default function EnquiryForm() {
  const [form, setForm] = useState<FormState>({ status: "idle", message: "" });
  const [token, setToken] = useState("");
  // Set when the verification widget cannot appear at all. While it is set the
  // form cannot be sent, so we do not offer a Send button that would only fail.
  const [verificationDown, setVerificationDown] = useState(false);
  // An answer is good for one send; bump this to start a fresh one.
  const [challengeKey, setChallengeKey] = useState(0);

  const handleUnavailable = (reason: string) => {
    console.error(`wholesale enquiry form: verification unavailable — ${reason}`);
    setVerificationDown(true);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setForm({ status: "success", message: "Thank you — we'll write back soon." });
      formEl.reset();
      return;
    }

    const message = String(formData.get("message") ?? "");
    if (message.length > 500) {
      setForm({ status: "error", message: "Message must be 500 characters or fewer." });
      return;
    }

    if (!token) {
      setForm({
        status: "error",
        message: "Please complete the verification below, then send again.",
      });
      return;
    }

    setForm({ status: "loading", message: "" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(formData.get("name") ?? ""),
          business: String(formData.get("business") ?? ""),
          email: String(formData.get("email") ?? ""),
          // Not a field on this form: every enquiry from this page is one thing,
          // and this is what puts that word in the subject line.
          topic: "Wholesale",
          message,
          altcha: token,
        }),
      });
      const body = await response.json().catch(() => ({}) as { ok?: boolean; error?: string });

      if (response.ok && body.ok === true) {
        setForm({
          status: "success",
          message: "Thank you — your enquiry is with us. We'll come back to you within one working day.",
        });
        formEl.reset();
      } else if (response.status >= 400 && response.status < 500 && body.error) {
        // Something the visitor can put right — say what it is, in place.
        setForm({ status: "error", message: body.error });
      } else {
        setForm({ status: "down", message: "" });
      }
    } catch {
      // Offline, blocked, the Function never answered — we do not know it sent,
      // so we must not say it did.
      setForm({ status: "down", message: "" });
    } finally {
      setToken("");
      setChallengeKey((key) => key + 1);
    }
  };

  return (
    <section
      id="wholesale-enquiry"
      className="w-full bg-background-100 py-24 md:py-32 px-6 md:px-10 lg:px-14"
    >
      <div className="max-w-3xl mx-auto">
        <div className="mb-12 reveal">
          <span className="text-xs tracking-[0.35em] uppercase text-accent-700">Start Here</span>
          <h2 className="font-heading text-4xl md:text-5xl text-primary-950 mt-4 mb-6 leading-tight">
            Tell us about <em>your cafe.</em>
          </h2>
          <p className="text-foreground-700 leading-relaxed">
            Four things is all we need to quote: who you are, where to write back, and a line about
            roughly how much coffee a week and where you are. A person reads every one of these.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-background-50 border border-background-200 rounded-lg p-8 reveal"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label
                htmlFor="wholesale-business"
                className="block text-xs uppercase tracking-widest text-foreground-600 mb-2"
              >
                Cafe or Business Name
              </label>
              <input
                id="wholesale-business"
                name="business"
                type="text"
                required
                className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-accent-500 transition-colors"
                placeholder="The Blue Door Cafe"
              />
            </div>
            <div>
              <label
                htmlFor="wholesale-name"
                className="block text-xs uppercase tracking-widest text-foreground-600 mb-2"
              >
                Your Name
              </label>
              <input
                id="wholesale-name"
                name="name"
                type="text"
                required
                className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-accent-500 transition-colors"
                placeholder="Anna Reyes"
              />
            </div>
          </div>

          <div className="mt-5">
            <label
              htmlFor="wholesale-email"
              className="block text-xs uppercase tracking-widest text-foreground-600 mb-2"
            >
              Email
            </label>
            <input
              id="wholesale-email"
              name="email"
              type="email"
              required
              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-accent-500 transition-colors"
              placeholder="anna@example.com"
            />
          </div>

          <div className="mt-5">
            <label
              htmlFor="wholesale-message"
              className="block text-xs uppercase tracking-widest text-foreground-600 mb-2"
            >
              Message
            </label>
            <textarea
              id="wholesale-message"
              name="message"
              required
              rows={5}
              maxLength={500}
              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-accent-500 transition-colors resize-none"
              placeholder="Roughly how much coffee a week, and where you are. How you pour it — espresso, batch brew, both — helps too."
            />
            <span className="text-xs text-foreground-500 mt-1 block">Up to 500 characters.</span>
          </div>

          {/* Honeypot */}
          <div className="wa-field-alt" aria-hidden="true">
            <label htmlFor="wholesale-website_alt">Website</label>
            <input
              id="wholesale-website_alt"
              name="website_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              readOnly
            />
          </div>

          {/* The check the Function performs, put where the visitor can satisfy it. */}
          <Altcha
            resetKey={challengeKey}
            onToken={setToken}
            onUnavailable={handleUnavailable}
          />

          {verificationDown && (
            <div className="mt-6">
              <ReachUs reason="This form cannot be sent at the moment." />
            </div>
          )}

          {form.status === "down" && !verificationDown && (
            <div className="mt-6">
              <ReachUs reason="Sorry — that enquiry did not go through." />
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <button
              type="submit"
              disabled={form.status === "loading" || verificationDown}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-900 text-background-50 text-sm font-medium hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span>{form.status === "loading" ? "Sending…" : "Send Enquiry"}</span>
              <i className="ri-send-plane-2-line w-4 h-4 flex items-center justify-center"></i>
            </button>

            {form.status === "success" && (
              <p className="text-sm text-accent-700 flex items-center gap-2">
                <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                {form.message}
              </p>
            )}
            {form.status === "error" && (
              <p className="text-sm text-secondary-700 flex items-center gap-2">
                <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                {form.message}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
